/**
 * Vehicle condition, guessed from the same accelerometer that measures roads.
 *
 * The question from the cab: "could the accelerometer see if there is an issue
 * with the car? like a bearing vibration or shock being bad and notify driver".
 * Honest answer: a wheel BEARING vibrates at 50-200+ Hz, far above what the
 * browser's ~50-60 Hz devicemotion stream can even represent, so that specific
 * fault cannot be seen directly. What IS inside the phone's band:
 *
 *  - WHEEL faults (imbalance, bent rim, cupped or separating tyre): vibration
 *    at wheel-rotation frequency, ~8-14 Hz at cruise. Road input is broadband;
 *    a wheel fault is a narrow peak that sits exactly at rotation speed. We
 *    measure the energy in a narrow band around the expected wheel frequency
 *    against the surrounding broadband, so the metric mostly cancels the road.
 *  - Worn DAMPERS: body bounce lives at 1-2 Hz. A healthy damper kills the
 *    oscillation within about a cycle after a bump; a worn one lets the cab
 *    keep floating. Every sharp bump is a free test pulse - we measure how
 *    much low-frequency motion is still ringing shortly after each one,
 *    normalised by the size of the bump itself.
 *  - The crude trend: overall vibration RMS normalised by speed. Road-
 *    dependent per trip, but over many trips on the usual corridors a vehicle
 *    that is getting rougher shows up here first.
 *
 * None of this diagnoses anything on its own. Each drive produces one small
 * record (see TripHealth in core/types.ts); the alert only fires when the
 * recent median for the SAME vehicle drifts well past its own history - a
 * "worth checking at the next service" nudge, never a verdict. Moving the
 * phone to a new mount or running loaded vs empty shifts the baseline too,
 * which is exactly why the thresholds are deliberately lazy.
 */

import type { TripHealth, Trip } from '../core/types';

/** Rolling raw-sample window kept for analysis, ms. */
const BUFFER_MS = 4000;
/** Analysis window length, ms. Long enough for ~0.9 Hz resolution. */
const WINDOW_MS = 2200;
/** Re-analyse this often, ms. */
const ANALYSE_EVERY_MS = 2500;
/** Below this the wheel-frequency test is off: the peak sits too low to separate. */
const WHEEL_MIN_KMH = 55;
/** Any slower and a "bump" is a door slam or a kerb, not a test pulse. */
const BUMP_MIN_KMH = 25;
/** Vertical jolt that counts as a bump, m/s². */
const BUMP_JOLT = 3.5;
/** Ignore further bumps for this long, so washboard doesn't count as ringing. */
const BUMP_REFRACTORY_MS = 3000;
/** Speed reading older than this is stale - stop analysing. */
const SPEED_TTL_MS = 4000;

/**
 * Rolling circumference used to place the wheel-rotation frequency, metres.
 * Approximate on purpose - the narrowband window below is wide enough to
 * swallow the difference between tyre sizes within a class.
 */
const CIRCUMFERENCE_M: Record<'car' | 'truck', number> = { car: 1.95, truck: 3.05 };

interface MotionSample {
  t: number;
  /** Magnitude of gravity-removed acceleration - orientation-independent. */
  mag: number;
  /** Component along gravity - the body-bounce axis, whatever way the phone sits. */
  vert: number;
}

/** Goertzel power of `values` (uniform rate `hz`) at frequency `f`. */
function goertzel(values: number[], hz: number, f: number): number {
  const n = values.length;
  const k = Math.round((n * f) / hz);
  const w = (2 * Math.PI * k) / n;
  const coeff = 2 * Math.cos(w);
  let s0 = 0;
  let s1 = 0;
  let s2 = 0;
  for (let i = 0; i < n; i++) {
    s0 = values[i] + coeff * s1 - s2;
    s2 = s1;
    s1 = s0;
  }
  return (s1 * s1 + s2 * s2 - coeff * s1 * s2) / (n * n);
}

/** Total Goertzel power over [from..to] Hz in ~1 Hz steps. */
function bandPower(values: number[], hz: number, from: number, to: number): number {
  let sum = 0;
  for (let f = from; f <= to; f += 1) sum += goertzel(values, hz, f);
  return sum;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[s.length >> 1] : 0;
}

export class VehicleHealthMonitor {
  private samples: MotionSample[] = [];
  private gravity: [number, number, number] = [0, 0, 9.81];
  private handler: ((e: DeviceMotionEvent) => void) | null = null;
  private running = false;
  private kind: 'car' | 'truck' = 'car';

  private lastSpeedKmh = 0;
  private speedAt = 0;
  private lastAnalysedAt = 0;
  private lastBumpAt = 0;
  /** A bump waiting for its ring-down window to fill. */
  private pendingBump: { t: number; jolt: number } | null = null;

  private wheelRatios: number[] = [];
  private ringScores: number[] = [];
  private vibSamples: number[] = [];

  /** Uses the same devicemotion permission the roughness meter already asked for. */
  start(kind: 'car' | 'truck'): void {
    if (this.running || typeof DeviceMotionEvent === 'undefined') return;
    this.kind = kind;
    this.running = true;
    // A drive too short to be logged never calls finish(), so its leftovers
    // would otherwise leak into the next trip's record. Every drive starts clean.
    this.wheelRatios = [];
    this.ringScores = [];
    this.vibSamples = [];
    this.handler = (e) => this.onMotion(e);
    window.addEventListener('devicemotion', this.handler);
  }

  stop(): void {
    if (this.handler) window.removeEventListener('devicemotion', this.handler);
    this.handler = null;
    this.running = false;
    this.samples = [];
    this.pendingBump = null;
  }

  setSpeed(kmh: number): void {
    this.lastSpeedKmh = kmh;
    this.speedAt = Date.now();
  }

  private onMotion(e: DeviceMotionEvent): void {
    const a = e.accelerationIncludingGravity;
    if (!a || a.x == null || a.y == null || a.z == null) return;

    const alpha = 0.9;
    this.gravity = [
      this.gravity[0] * alpha + a.x * (1 - alpha),
      this.gravity[1] * alpha + a.y * (1 - alpha),
      this.gravity[2] * alpha + a.z * (1 - alpha),
    ];
    const [gx, gy, gz] = this.gravity;
    const g = Math.hypot(gx, gy, gz) || 9.81;
    const dx = a.x - gx;
    const dy = a.y - gy;
    const dz = a.z - gz;

    const now = Date.now();
    this.samples.push({
      t: now,
      mag: Math.hypot(dx, dy, dz),
      // Projection onto the gravity vector = "up and down" regardless of how
      // the phone is cradled. This is the axis dampers act on.
      vert: (dx * gx + dy * gy + dz * gz) / g,
    });
    const cutoff = now - BUFFER_MS;
    while (this.samples.length && this.samples[0].t < cutoff) this.samples.shift();

    const speedFresh = now - this.speedAt <= SPEED_TTL_MS;
    if (!speedFresh) return;

    // Bump detection runs on every sample so the pulse itself is never missed.
    const latest = this.samples[this.samples.length - 1];
    if (
      Math.abs(latest.vert) >= BUMP_JOLT &&
      this.lastSpeedKmh >= BUMP_MIN_KMH &&
      now - this.lastBumpAt > BUMP_REFRACTORY_MS &&
      !this.pendingBump
    ) {
      this.lastBumpAt = now;
      this.pendingBump = { t: now, jolt: Math.abs(latest.vert) };
    }
    if (this.pendingBump && now - this.pendingBump.t >= 2000) {
      this.scoreBump(this.pendingBump);
      this.pendingBump = null;
    }

    if (now - this.lastAnalysedAt >= ANALYSE_EVERY_MS) {
      this.lastAnalysedAt = now;
      this.analyseWindow(now);
    }
  }

  /** Spectral look at the last WINDOW_MS of driving. */
  private analyseWindow(now: number): void {
    const from = now - WINDOW_MS;
    const win = this.samples.filter((s) => s.t >= from);
    if (win.length < 48) return;
    const spanS = (win[win.length - 1].t - win[0].t) / 1000;
    if (spanS < 1.2) return;
    const hz = win.length / spanS;
    // A tab that throttles devicemotion to a crawl cannot support any of this.
    if (hz < 25) return;

    const mags = win.map((s) => s.mag);

    // Overall trend sample, normalised the same way roughness.ts does so the
    // figure does not simply say "we drove faster today".
    if (this.lastSpeedKmh >= 30) {
      let sum = 0;
      for (const m of mags) sum += m * m;
      const rms = Math.sqrt(sum / mags.length);
      this.vibSamples.push(rms / Math.sqrt(Math.max(30, this.lastSpeedKmh) / 60));
    }

    // Wheel-frequency peak vs the broadband around it.
    if (this.lastSpeedKmh >= WHEEL_MIN_KMH) {
      const wheelHz = this.lastSpeedKmh / 3.6 / CIRCUMFERENCE_M[this.kind];
      // Must sit inside the analysable band, clear of both the body-bounce
      // region below and the Nyquist limit above.
      if (wheelHz >= 4.5 && wheelHz <= hz * 0.4) {
        const narrow =
          goertzel(mags, hz, wheelHz - 0.5) +
          goertzel(mags, hz, wheelHz) +
          goertzel(mags, hz, wheelHz + 0.5);
        const broad = bandPower(mags, hz, 4, Math.min(20, hz * 0.45));
        if (broad > 1e-9) this.wheelRatios.push(narrow / broad);
      }
    }
  }

  /**
   * How much the body is still oscillating 0.45-1.9 s after a bump, relative
   * to the bump itself. By then a healthy damper has eaten the motion; what
   * remains is the floating that worn dampers are famous for.
   */
  private scoreBump(bump: { t: number; jolt: number }): void {
    const win = this.samples.filter((s) => s.t >= bump.t + 450 && s.t <= bump.t + 1900);
    if (win.length < 32) return;
    const spanS = (win[win.length - 1].t - win[0].t) / 1000;
    if (spanS < 1) return;
    const hz = win.length / spanS;
    if (hz < 25) return;
    const verts = win.map((s) => s.vert);
    // Body bounce band only - tarmac texture above it must not count as ringing.
    const low = bandPower(verts, hz, 1, 2) + goertzel(verts, hz, 2.5);
    this.ringScores.push(Math.sqrt(low) / bump.jolt);
  }

  /** Roll the drive up into one record, or null if nothing was measurable. */
  finish(): TripHealth | null {
    const health: TripHealth = {
      wheelN: this.wheelRatios.length,
      damperN: this.ringScores.length,
      vibN: this.vibSamples.length,
    };
    const r = (n: number) => Math.round(n * 1000) / 1000;
    if (health.wheelN >= 20) health.wheel = r(median(this.wheelRatios));
    if (health.damperN >= 5) health.damper = r(median(this.ringScores));
    if (health.vibN >= 40) health.vib = r(median(this.vibSamples));
    this.wheelRatios = [];
    this.ringScores = [];
    this.vibSamples = [];
    return health.wheel != null || health.damper != null || health.vib != null ? health : null;
  }
}

export type HealthDrift = 'wheel' | 'damper' | 'vib';

/**
 * Minimum sample counts for a trip's metric to join the trend at all. Below
 * these the number is one noisy guess, not a measurement.
 */
const TRUST_N: Record<HealthDrift, number> = { wheel: 20, damper: 5, vib: 40 };
/** Recent median must exceed baseline × factor AND an absolute floor to fire. */
const DRIFT_FACTOR: Record<HealthDrift, number> = { wheel: 1.6, damper: 1.5, vib: 1.35 };
const DRIFT_FLOOR: Record<HealthDrift, number> = { wheel: 0.22, damper: 0.3, vib: 0.05 };
/** How many trips form "now" and how many form "before". */
const RECENT_TRIPS = 3;
const BASELINE_MIN = 5;
const BASELINE_MAX = 12;

/**
 * Has any metric drifted clearly above this vehicle's own history?
 *
 * `trips` newest-first (as core/db.ts returns them). Only trips of the SAME
 * vehicle count - the truck's baseline says nothing about the car. Returns the
 * highest-priority drifted metric, or null. Wheel outranks damper outranks the
 * crude trend, because it is the most specific and the most actionable.
 */
export function assessHealthDrift(trips: Trip[], vehicleId: string): HealthDrift | null {
  const mine = trips.filter((t) => t.vehicleId === vehicleId && t.health);

  for (const metric of ['wheel', 'damper', 'vib'] as const) {
    const values = mine
      .filter((t) => t.health![metric] != null && t.health![`${metric}N`] >= TRUST_N[metric])
      .map((t) => t.health![metric]!);
    if (values.length < RECENT_TRIPS + BASELINE_MIN) continue;

    const recent = median(values.slice(0, RECENT_TRIPS));
    const baseline = median(values.slice(RECENT_TRIPS, RECENT_TRIPS + BASELINE_MAX));
    if (baseline <= 0) continue;
    if (recent >= baseline * DRIFT_FACTOR[metric] && recent >= DRIFT_FLOOR[metric]) {
      return metric;
    }
  }
  return null;
}
